import React, { useContext, useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import { makeStyles } from "tss-react/mui";
import CheckIcon from "@mui/icons-material/Check";
import { useHistory, useLocation } from "react-router-dom";
import { BRAND, FONT, RADIUS, SHADOW, TEXTURE_URL, useLightStyles, MEDIA } from "./lightTheme";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import { Context } from "states/Store";
import { createDossier, updateRequiredDocuments } from "services/edge/DossierService";
import { addThirdparty, getTPContacts, createTPContacts } from "services/edge/ThirdpartyService";
import { getAccountConsumption } from "services/edge/ConsumptionService";
import PersonAddOutlinedIcon from "@mui/icons-material/PersonAddOutlined";
import i18next from "i18next";
import { addThirdpartyAttribute, addDossierAttribute } from "services/edge/AttributesService";
import { getApiErrorMessageCode } from "services/common/ApiUtils";
import { ERROR, getUserLanguages, REGEXP_EMAIL } from "services/common/Constants";
import { getUrlWithAccountId } from "config/Routes";
import StepChooseTP from "./steps/StepChooseTP";
import StepChooseDossier from "./steps/StepChooseDossier";
import StepConfirm from "./steps/StepConfirm";
import StepChooseContext from "./steps/StepChooseContext";
import { saveContextInstance, saveContextInstanceThirdparty, createThirdPartyDossier } from "services/edge/ContexteService";

const useStyles = makeStyles()(() => ({
  wrapper: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    flex: 1,
    fontFamily: "Inter, sans-serif",
  },
  // ── Stepper header ──
  stepperBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "1.1rem 2rem",
    borderBottom: `1px solid ${BRAND.border}`,
    [MEDIA.smDown]: { padding: "0.9rem 1rem" },
  },
  stepperInnerResponsive: {
    [MEDIA.smDown]: { width: "100%", overflowX: "auto", WebkitOverflowScrolling: "touch" },
  },
  stepperInner: {
    display: "flex",
    alignItems: "center",
    gap: "0",
  },
  stepItem: {
    display: "flex",
    alignItems: "center",
    gap: "0.75rem",
    padding: "0 0.5rem",
    position: "relative",
    cursor: "default",
  },
  stepDot: {
    width: "36px",
    height: "36px",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "0.875rem",
    fontWeight: 700,
    fontFamily: "Inter, sans-serif",
    flexShrink: 0,
    transition: "background 0.2s, box-shadow 0.2s",
    [MEDIA.smDown]: { width: "30px", height: "30px", fontSize: "0.8rem" },
  },
  stepDotActive: {
    backgroundColor: BRAND.primary,
    color: BRAND.white,
    boxShadow: `0 0 0 5px ${BRAND.tertiary}`,
  },
  stepDotDone: {
    backgroundColor: BRAND.secondary,
    color: BRAND.primary,
  },
  stepDotIdle: {
    backgroundColor: BRAND.background,
    color: BRAND.textMuted,
    border: `2px solid ${BRAND.border}`,
  },
  stepLabel: {
    fontSize: "0.9rem",
    fontFamily: "Inter, sans-serif",
    fontWeight: 500,
    transition: "color 0.2s",
    whiteSpace: "nowrap",
    [MEDIA.smDown]: { display: "none" },
  },
  stepLabelActive: { color: BRAND.textMain, fontWeight: 700 },
  stepLabelDone:   { color: BRAND.textMuted },
  stepLabelIdle:   { color: BRAND.textMuted },
  stepSeparator: {
    width: "64px",
    height: "2px",
    backgroundColor: BRAND.border,
    flexShrink: 0,
    margin: "0 0.5rem",
    borderRadius: "2px",
    transition: "background 0.3s",
    [MEDIA.smDown]: { width: "28px", margin: "0 0.25rem" },
  },
  stepSeparatorDone: {
    backgroundColor: BRAND.secondary,
  },
}));

function StepIndicator({ index, current, steps }) {
  const { classes: s, cx } = useStyles();
  const isDone   = index < current;
  const isActive = index === current;

  return (
    <Box className={s.stepItem}>
      <Box className={cx(
        s.stepDot,
        isDone   ? s.stepDotDone   :
        isActive ? s.stepDotActive :
                   s.stepDotIdle
      )}>
        {isDone ? <CheckIcon style={{ fontSize: "16px" }} /> : index + 1}
      </Box>
      <Typography className={cx(
        s.stepLabel,
        isDone   ? s.stepLabelDone   :
        isActive ? s.stepLabelActive :
                   s.stepLabelIdle
      )}>
        {steps[index].label()}
      </Typography>
    </Box>
  );
}

function ContactPromptScreen({ accountId, thirdpartyId, thirdpartyName, dispatch, onDone, onSkip }) {
  const { classes: light } = useLightStyles();
  const [email, setEmail] = useState("");
  const [lang, setLang] = useState(i18next.language || "fr");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const handleInvite = async () => {
    const trimmed = email.trim();
    if (!trimmed) return;
    if (!REGEXP_EMAIL.test(trimmed)) { setError(i18next.t("light.profile.invalidEmail")); return; }
    setError("");
    setSending(true);
    try {
      await createTPContacts(accountId, thirdpartyId, { email: trimmed, lang }, dispatch);
      setSent(true);
    } catch (e) {
      const errMsg = e?.message?.messages?.error?.[0]?.message;
      const errCode = e?.message?.messages?.error?.[0]?.code;
      if (errMsg?.includes("Blacklisted")) {
        setError(i18next.t("infos.EMAIL_BLACKLISTED_ERROR"));
      } else if (errMsg?.includes("does not wish to have any additional contacts")) {
        setError(i18next.t("infos.COMPTE_BLACKLISTED_ERROR"));
      } else if (errMsg?.includes("Invalid email")) {
        setError(i18next.t("light.profile.invalidEmail"));
      } else if (errCode === "E133") {
        const details = errMsg?.match(/\(([^)]+)\)/)?.[1] ?? "";
        setError(i18next.t("invalidEmail", { details }));
      } else {
        setError(getApiErrorMessageCode(e));
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <Box className={light.pageRoot}>
      <Box className={light.pageHeader}>
        <Box className={light.pageHeaderInner} style={{ maxWidth: "1100px" }}>
          <Typography className={light.pageTitleWhite}>{i18next.t("light.launch.lastStep")}</Typography>
          <Typography className={light.pageSubtitleWhite}>
            {i18next.t("light.launch.evalLaunched")}
          </Typography>
        </Box>
      </Box>
      <Box className={light.pageInner} style={{ maxWidth: "1100px", marginTop: "-0.75rem" }}>

        <Box className={light.card}>
          {/* Header — same pattern as attr form */}
          <Box style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "1.25rem" }}>
            <Box className={light.iconWrap} style={{ backgroundColor: `${BRAND.primary}10` }}>
              <PersonAddOutlinedIcon style={{ fontSize: "20px", color: BRAND.primary }} />
            </Box>
            <Box>
              <Typography style={{ ...FONT.h3, color: BRAND.textMain, fontFamily: FONT.family }}>{thirdpartyName || i18next.t("light.common.supplier")}</Typography>
              <Typography style={{ ...FONT.small, color: BRAND.textMuted, fontFamily: FONT.family }}>{i18next.t("light.launch.inviteContact")}</Typography>
            </Box>
          </Box>

          {!sent ? (
            <>
              <Typography style={{ ...FONT.body, color: BRAND.textMuted, fontFamily: FONT.family, lineHeight: 1.6, marginBottom: "1.25rem" }}>
                <span dangerouslySetInnerHTML={{ __html: i18next.t("light.launch.inviteMessage", { name: thirdpartyName || i18next.t("light.common.supplier") }) }} />
              </Typography>

              <Box style={{ display: "flex", gap: "0.75rem", alignItems: "flex-start", marginBottom: "0.5rem", flexWrap: "wrap" }}>
                <Box style={{ flex: 1, minWidth: "200px" }}>
                  <Typography style={{ ...FONT.label, color: BRAND.textMuted, fontFamily: FONT.family, marginBottom: "0.35rem" }}>
                    {i18next.t("light.launch.contactEmail")}
                  </Typography>
                  <input
                    type="email"
                    placeholder="nom@entreprise.com"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); if (error) setError(""); }}
                    onKeyDown={(e) => e.key === "Enter" && handleInvite()}
                    className={light.fieldInput}
                    style={{ width: "100%", boxSizing: "border-box", borderColor: error ? "#DC2626" : undefined }}
                    autoFocus
                  />
                </Box>
                <Box>
                  <Typography style={{ ...FONT.label, color: BRAND.textMuted, fontFamily: FONT.family, marginBottom: "0.35rem" }}>
                    {i18next.t("light.profile.language")}
                  </Typography>
                  <select
                    value={lang}
                    onChange={(e) => setLang(e.target.value)}
                    style={{ border: `1.5px solid ${BRAND.border}`, borderRadius: RADIUS.sm, padding: "0.55rem 0.5rem", fontFamily: FONT.family, fontSize: "0.85rem", outline: "none", backgroundColor: BRAND.white, cursor: "pointer" }}
                  >
                    {getUserLanguages().map((l) => (
                      <option key={l.code} value={l.code}>{l.name}</option>
                    ))}
                  </select>
                </Box>
              </Box>
              {error && (
                <Typography style={{ ...FONT.small, color: "#DC2626", fontFamily: FONT.family, marginBottom: "0.5rem" }}>{error}</Typography>
              )}

              {/* Actions */}
              <Box style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "1.5rem" }}>
                <button className={light.btnSecondary} style={{ padding: "0.5rem 1.25rem" }} onClick={onSkip}>
                  {i18next.t("light.launch.skipInvite")}
                </button>
                <button
                  className={light.btnPrimary}
                  style={{ padding: "0.5rem 1.5rem" }}
                  onClick={handleInvite}
                  disabled={!email.trim() || sending}
                >
                  {sending ? i18next.t("light.launch.inviting") : i18next.t("light.launch.invite")}
                </button>
              </Box>
            </>
          ) : (
            <Box style={{ textAlign: "center", padding: "1.5rem 0" }}>
              <Box style={{ width: "48px", height: "48px", borderRadius: "50%", backgroundColor: "#DCFCE7", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 1rem" }}>
                <CheckIcon style={{ fontSize: "24px", color: "#15803D" }} />
              </Box>
              <Typography style={{ ...FONT.h2, color: BRAND.textMain, fontFamily: FONT.family, marginBottom: "0.4rem" }}>
                {i18next.t("light.launch.invited")}
              </Typography>
              <Typography style={{ ...FONT.body, color: BRAND.textMuted, fontFamily: FONT.family }}>
                {i18next.t("light.launch.emailSent", { email })}
              </Typography>
              <Box style={{ marginTop: "1.5rem" }}>
                <button className={light.btnPrimary} style={{ padding: "0.5rem 1.5rem" }} onClick={onDone}>
                  {i18next.t("light.launch.goToTp")}
                </button>
              </Box>
            </Box>
          )}
        </Box>

      </Box>
    </Box>
  );
}

export default function LightLaunch() {
  const { classes: s, cx } = useStyles();
  const [state, dispatch] = useContext(Context);
  const history = useHistory();
  const location = useLocation();
  const preSelectedTP = location.state?.thirdparty ?? null;
  const preSelectedContext = location.state?.preselectedContext ?? null;

  // Context models — step context is shown only when account has models
  const hasContextModels = (state.contextModels ?? []).length > 0;

  // Dynamic steps based on whether context step exists
  const STEPS = hasContextModels ? [
    { label: () => i18next.t("light.launch.step1") },
    { label: () => (state.contextModels ?? []).map((m) => m.name).join(" / ") || i18next.t("light.launch.stepContext") },
    { label: () => i18next.t("light.launch.step2") },
    { label: () => i18next.t("light.launch.step3") },
  ] : [
    { label: () => i18next.t("light.launch.step1") },
    { label: () => i18next.t("light.launch.step2") },
    { label: () => i18next.t("light.launch.step3") },
  ];

  // Step indices — context step sits between TP and Dossier (only if hasContextModels)
  const STEP_TP      = 0;
  const STEP_CONTEXT = hasContextModels ? 1 : -1;
  const STEP_DOSSIER = hasContextModels ? 2 : 1;
  const STEP_CONFIRM = hasContextModels ? 3 : 2;

  const [step, setStep] = useState(
    preSelectedTP
      ? (hasContextModels && !preSelectedContext ? STEP_CONTEXT : STEP_DOSSIER)
      : STEP_TP,
  );
  const [selectedTP, setSelectedTP] = useState(preSelectedTP);
  const [selectedContext, setSelectedContext] = useState(preSelectedContext); // { instanceId?, isNew, modelId?, modelName?, reference }
  const [selectedDossierTypes, setSelectedDossierTypes] = useState([]);
  const [launching, setLaunching] = useState(false);
  const [launchSteps, setLaunchSteps] = useState([]);
  const [backFromConfirm, setBackFromConfirm] = useState(false);
  const [showContactPrompt, setShowContactPrompt] = useState(false);
  const [createdThirdpartyId, setCreatedThirdpartyId] = useState(null);
  const [reopenAttrs, setReopenAttrs] = useState(null);
  const [thresholdReached, setThresholdReached] = useState(false);
  const [thresholdValue, setThresholdValue] = useState(null);

  // Check consumption threshold on mount
  useEffect(() => {
    if (!state.account?.id) return;
    getAccountConsumption(state.account.id, dispatch)
      .then((r) => {
        const c = r?.content;
        if (c && c.status === "REACHED") {
          setThresholdReached(true);
          setThresholdValue(c.thirdPartyThreshold);
        }
      })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.account?.id]);

  // Scroll to top on mount and on step change
  useEffect(() => { window.scrollTo(0, 0); }, [step]);

  const handleTPSelected = (tp) => {
    setSelectedTP(tp);
    setReopenAttrs(null);
    setStep(hasContextModels && !selectedContext ? STEP_CONTEXT : STEP_DOSSIER);
  };

  const handleContextSelected = (ctx) => {
    setSelectedContext(ctx);
    setStep(STEP_DOSSIER);
  };

  const handleContextSkipped = () => {
    setSelectedContext(null);
    setStep(STEP_DOSSIER);
  };

  const handleDossierSelected = (dossierTypes) => {
    setSelectedDossierTypes(Array.isArray(dossierTypes) ? dossierTypes : [dossierTypes]);
    setStep(STEP_CONFIRM);
  };

  const handleLaunch = async () => {
    if (!selectedTP || selectedDossierTypes.length === 0) return;

    const showContextStep = selectedContext?.isNew;
    const dossierCount = selectedDossierTypes.length;
    const steps = [
      ...(!selectedTP.onboarded ? [{ label: i18next.t("light.launch.stepAddTp"), status: "pending" }] : []),
      ...selectedDossierTypes.map((dt) => ({ label: `${i18next.t("light.launch.stepPrepareEval")} — ${dt.libelle}`, status: "pending" })),
      { label: i18next.t("light.launch.stepConfigDocs"), status: "pending" },
      ...(showContextStep ? [{ label: i18next.t("light.launch.stepCreateContext", { model: selectedContext.modelName }), status: "pending" }] : []),
    ];
    setLaunchSteps(steps);
    setLaunching(true);

    const withMinDelay = (promise, ms = 800) => Promise.all([promise, new Promise((r) => setTimeout(r, ms))]).then(([res]) => res);

    try {
      let thirdpartyId = selectedTP.thirdpartyId;
      let contextInstanceId = selectedContext?.instanceId ?? null;
      let stepIdx = 0;

      // Step: add thirdparty
      if (!selectedTP.onboarded) {
        setLaunchSteps((prev) => prev.map((s, j) => ({ ...s, status: j === stepIdx ? "active" : s.status })));
        const addRes = await withMinDelay(addThirdparty(state.account.id, {
          companyIdType: selectedTP.rawId?.length === 14 ? "CIT001" : "CIT003",
          companyIdValue: selectedTP.rawId,
          companyIdEncrypted: selectedTP.isIdEncrypted,
        }, dispatch));
        thirdpartyId = addRes?.content?.thirdpartyId;
        if (!thirdpartyId) throw new Error("TP_ADD_FAILED");

        const attrs = selectedTP.pendingAttributes ?? [];
        await Promise.all(
          attrs.filter((a) => a.values?.length > 0)
            .map((a) => addThirdpartyAttribute(state.account.id, thirdpartyId, { label: a.label, values: a.values }, dispatch))
        );
        setLaunchSteps((prev) => prev.map((s, j) => ({ ...s, status: j === stepIdx ? "done" : s.status })));
        stepIdx++;
      }

      // Steps: create dossiers (one per selected type)
      const dossierIds = [];
      const hasContext = !!selectedContext;

      if (!hasContext) {
        // No context: create dossiers directly
        for (let i = 0; i < dossierCount; i++) {
          const dt = selectedDossierTypes[i];
          const currentStep = stepIdx;
          setLaunchSteps((prev) => prev.map((s, j) => ({ ...s, status: j === currentStep ? "active" : s.status })));
          const res = await withMinDelay(createDossier(state.account.id, thirdpartyId, {
            dossierTypeCode: dt.code,
            dossierReference: crypto.randomUUID(),
            expirationDate: null,
            description: "",
          }, dispatch), dossierCount > 1 ? 500 : 800);
          const dossierId = res?.content?.dossierId;
          if (dossierId) {
            dossierIds.push({ dossierId, dt });
            const extras = dt.extraDocs ?? [];
            if (extras.length > 0) {
              const baseCodes = (dt.requirements ?? []).map((r) => r.code);
              await updateRequiredDocuments(state.account.id, dossierId, { newRequiredDocuments: [...baseCodes, ...extras] }, dispatch);
            }
          }
          setLaunchSteps((prev) => prev.map((s, j) => ({ ...s, status: j === currentStep ? "done" : s.status })));
          stepIdx++;
        }
      }

      // With context: create context first, then create dossiers via context API
      if (hasContext) {
        try {
          if (selectedContext.isNew) {
            const ctxStep = stepIdx;
            setLaunchSteps((prev) => prev.map((s, j) => ({ ...s, status: j === ctxStep ? "active" : s.status })));
            contextInstanceId = await saveContextInstance(state.account.id, {
              contextModelName: selectedContext.modelName,
              reference: selectedContext.reference,
              description: selectedContext.description || "",
              expirationDate: selectedContext.expirationDate || null,
              contextAttributes: selectedContext.contextAttributes ?? [],
            }, dispatch);
            setLaunchSteps((prev) => prev.map((s, j) => ({ ...s, status: j === ctxStep ? "done" : s.status })));
            stepIdx++;
            await new Promise((r) => setTimeout(r, 400));
          }
          if (contextInstanceId) {
            const tiersAttributes = (selectedContext.pendingTiersAttributes ?? [])
              .filter((a) => a.values?.length > 0 && a.values[0] !== "")
              .map((a) => ({ attributeId: a.attributeId, label: a.label, values: a.values }));
            const contextThirdpartyIds = await saveContextInstanceThirdparty(state.account.id, [{
              thirdPartyId: thirdpartyId,
              contextInstanceId,
              parentId: -1,
              tiersAttributes,
              referents: [],
            }], dispatch);
            const contextThirdpartyId = Array.isArray(contextThirdpartyIds) ? contextThirdpartyIds[0] : contextThirdpartyIds;
            if (contextThirdpartyId) {
              for (let i = 0; i < dossierCount; i++) {
                const dt = selectedDossierTypes[i];
                const currentStep = stepIdx;
                setLaunchSteps((prev) => prev.map((s, j) => ({ ...s, status: j === currentStep ? "active" : s.status })));
                const res = await createThirdPartyDossier(state.account.id, thirdpartyId, contextThirdpartyId, {
                  dossierTypeCode: dt.code,
                  dossierReference: crypto.randomUUID(),
                  expirationDate: null,
                  description: "",
                }, dispatch);
                const dossierId = res?.content?.dossierId;
                if (dossierId) {
                  dossierIds.push({ dossierId, dt });
                  const extras = dt.extraDocs ?? [];
                  if (extras.length > 0) {
                    const baseCodes = (dt.requirements ?? []).map((r) => r.code);
                    await updateRequiredDocuments(state.account.id, dossierId, { newRequiredDocuments: [...baseCodes, ...extras] }, dispatch);
                  }
                }
                setLaunchSteps((prev) => prev.map((s, j) => ({ ...s, status: j === currentStep ? "done" : s.status })));
                stepIdx++;
              }
            }
          }
        } catch (_) {
          // Context/dossier creation failure
        }
      }

      if (dossierIds.length > 0) {
        // Step: configure attributes for all dossiers
        setLaunchSteps((prev) => prev.map((s, j) => ({ ...s, status: j === stepIdx ? "active" : s.status })));
        const attrPromises = dossierIds.flatMap(({ dossierId, dt }) => {
          const dossierAttrs = dt.pendingDossierAttributes ?? [];
          return dossierAttrs.filter((a) => a.values?.length > 0)
            .map((a) => addDossierAttribute(state.account.id, dossierId, { label: a.label, values: a.values }, dispatch));
        });
        await withMinDelay(Promise.all(attrPromises));
        setLaunchSteps((prev) => prev.map((s, j) => ({ ...s, status: j === stepIdx ? "done" : s.status })));

        await new Promise((r) => setTimeout(r, 600));

        try {
          const contactsRes = await getTPContacts(state.account.id, thirdpartyId, dispatch);
          const hasInvitedContact = (contactsRes?.content ?? []).some((c) => c.status === "INVITE" || c.status === "INSCRIT");
          if (!hasInvitedContact) {
            setCreatedThirdpartyId(thirdpartyId);
            setShowContactPrompt(true);
            setLaunching(false);
            setLaunchSteps([]);
            return;
          }
        } catch (_) {}

        history.push(getUrlWithAccountId(state.account.id, `light/thirdparties/${thirdpartyId}`));
      }
    } catch (e) {
      dispatch({ type: "ALERT", alert: { type: ERROR, msg: getApiErrorMessageCode(e) } });
    } finally {
      setLaunching(false);
      setLaunchSteps([]);
    }
  };

  // Threshold reached — blocking screen
  if (thresholdReached) {
    return (
      <Box style={{ display: "flex", flexDirection: "column", width: "100%" }}>
        {/* Hero */}
        <Box style={{
          width: "100%", padding: "3rem 2rem 4rem",
          backgroundColor: BRAND.primary,
          backgroundImage: `url(${TEXTURE_URL})`, backgroundSize: "cover", backgroundPosition: "center",
          display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center",
        }}>
          <Typography style={{ ...FONT.h1, color: "#fff", fontFamily: FONT.family }}>
            {i18next.t("light.home.launchTitle")}
          </Typography>
        </Box>
        {/* Card */}
        <Box style={{ maxWidth: "520px", width: "100%", margin: "-2rem auto 0", padding: "0 1.5rem", boxSizing: "border-box" }}>
          <Box style={{
            backgroundColor: BRAND.white, borderRadius: RADIUS.lg, border: `1.5px solid ${BRAND.border}`,
            boxShadow: SHADOW.medium, padding: "2.5rem 2rem", textAlign: "center",
            display: "flex", flexDirection: "column", alignItems: "center", gap: "1.25rem",
          }}>
            {/* Illustration */}
            <Box style={{
              width: "72px", height: "72px", borderRadius: "50%",
              backgroundColor: "#FEF3C7", display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#B45309" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                <line x1="12" y1="9" x2="12" y2="13" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
            </Box>
            <Typography style={{ ...FONT.h2, color: BRAND.textMain, fontFamily: FONT.family }}>
              {i18next.t("light.threshold.title")}
            </Typography>
            <Typography style={{ ...FONT.body, color: BRAND.textMuted, fontFamily: FONT.family, lineHeight: 1.6 }}>
              {i18next.t("light.threshold.message", { threshold: thresholdValue })}
            </Typography>
            <Typography style={{ ...FONT.small, color: BRAND.textMuted, fontFamily: FONT.family, lineHeight: 1.6 }}>
              {i18next.t("light.threshold.contact")}
            </Typography>
            <button
              className={s.btnBack || undefined}
              onClick={() => history.goBack()}
              style={{
                marginTop: "0.5rem", padding: "0.6rem 1.5rem", borderRadius: RADIUS.md,
                backgroundColor: BRAND.primary, color: BRAND.white, border: "none", cursor: "pointer",
                ...FONT.h3, fontFamily: FONT.family,
              }}
            >
              {"← " + i18next.t("back")}
            </button>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box className={s.wrapper}>
      {/* Launch loader overlay */}
      {launching && launchSteps.length > 0 && (
        <Box style={{ display: "flex", flexDirection: "column", alignItems: "center", width: "100%" }}>
          {/* Hero banner */}
          <Box style={{
            width: "100%", padding: "2.5rem 2rem 3.5rem",
            backgroundColor: BRAND.primary,
            backgroundImage: `url(${TEXTURE_URL})`, backgroundSize: "cover", backgroundPosition: "center",
            display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", gap: "0.5rem",
            boxSizing: "border-box",
          }}>
            <Typography style={{ ...FONT.h1, color: BRAND.white, fontFamily: FONT.family }}>
              {i18next.t("light.launch.launching")}
            </Typography>
            <Typography style={{ ...FONT.body, color: "rgba(255,255,255,0.7)", fontFamily: FONT.family }}>
              {selectedDossierTypes.map((dt) => dt.libelle).join(", ")} — {selectedTP?.name}
            </Typography>
          </Box>

          {/* Card with spinner + steps */}
          <Box style={{
            maxWidth: "1100px", width: "100%", marginTop: "-2rem",
            backgroundColor: BRAND.white, borderRadius: RADIUS.lg,
            border: `1px solid ${BRAND.border}`, boxShadow: SHADOW.medium,
            padding: "2rem", display: "flex", flexDirection: "column", alignItems: "center", gap: "1.5rem",
          }}>
            {/* Animated ring */}
            <Box style={{ position: "relative", width: "72px", height: "72px" }}>
              <Box style={{
                width: "72px", height: "72px", borderRadius: "50%",
                border: `3px solid ${BRAND.border}`,
                borderTopColor: BRAND.primary,
                animation: "launchSpin 0.9s linear infinite",
              }} />
              <Box style={{
                position: "absolute", top: "50%", left: "50%",
                transform: "translate(-50%, -50%)",
                width: "40px", height: "40px", borderRadius: "50%",
                backgroundColor: BRAND.primary,
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <FolderOutlinedIcon style={{ fontSize: "18px", color: BRAND.white }} />
              </Box>
            </Box>
            <style>{`@keyframes launchSpin { to { transform: rotate(360deg); } }`}</style>

            {/* Steps list */}
            <Box style={{ display: "flex", flexDirection: "column", gap: "0.6rem", width: "100%" }}>
              {launchSteps.map((ls, i) => (
                <Box key={i} style={{
                  display: "flex", alignItems: "center", gap: "0.75rem",
                  padding: "0.65rem 1rem", borderRadius: RADIUS.md,
                  backgroundColor: ls.status === "active" ? `${BRAND.primary}08` : "transparent",
                  border: `1.5px solid ${ls.status === "active" ? BRAND.primary : ls.status === "done" ? "#15803D" : BRAND.border}`,
                  transition: "all 0.3s",
                }}>
                  {ls.status === "done" ? (
                    <Box style={{
                      width: "24px", height: "24px", borderRadius: "50%",
                      backgroundColor: "#DCFCE7", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                    }}>
                      <CheckIcon style={{ fontSize: "14px", color: "#15803D" }} />
                    </Box>
                  ) : ls.status === "active" ? (
                    <Box style={{
                      width: "24px", height: "24px", borderRadius: "50%",
                      border: `2px solid ${BRAND.primary}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                      animation: "launchPulse 1.2s ease-in-out infinite",
                    }}>
                      <Box style={{ width: "8px", height: "8px", borderRadius: "50%", backgroundColor: BRAND.primary }} />
                    </Box>
                  ) : (
                    <Box style={{
                      width: "24px", height: "24px", borderRadius: "50%",
                      border: `2px solid ${BRAND.border}`, flexShrink: 0,
                    }} />
                  )}
                  <Typography style={{
                    ...FONT.body, fontFamily: FONT.family,
                    fontWeight: ls.status === "active" ? 600 : 500,
                    color: ls.status === "pending" ? BRAND.textMuted : BRAND.textMain,
                    transition: "color 0.3s",
                  }}>
                    {ls.label}
                  </Typography>
                </Box>
              ))}
            </Box>
            <style>{`@keyframes launchPulse { 0%, 100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.6; transform: scale(0.92); } }`}</style>

            {/* Illustration */}
            <Box style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "1.25rem", opacity: 0.5, marginTop: "0.5rem" }}>
              <Box style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "0.3rem" }}>
                <Box style={{ width: "44px", height: "44px", borderRadius: RADIUS.md, backgroundColor: `${BRAND.primary}10`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <BusinessOutlinedIcon style={{ fontSize: "22px", color: BRAND.primary }} />
                </Box>
                <Typography style={{ ...FONT.meta, color: BRAND.textMuted, fontFamily: FONT.family }}>{i18next.t("light.common.supplier")}</Typography>
              </Box>
              <svg width="48" height="16" viewBox="0 0 48 16" fill="none" style={{ flexShrink: 0 }}>
                <path d="M0 8h40M36 3l6 5-6 5" stroke={BRAND.primary} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" strokeDasharray="4 3">
                  <animate attributeName="stroke-dashoffset" from="28" to="0" dur="1.5s" repeatCount="indefinite" />
                </path>
              </svg>
              <Box style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "0.3rem" }}>
                <Box style={{ width: "44px", height: "44px", borderRadius: RADIUS.md, backgroundColor: `${BRAND.primary}10`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <FolderOutlinedIcon style={{ fontSize: "22px", color: BRAND.primary }} />
                </Box>
                <Typography style={{ ...FONT.meta, color: BRAND.textMuted, fontFamily: FONT.family }}>{i18next.t("light.common.evaluation")}</Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      )}

      {!showContactPrompt && !(launching && launchSteps.length > 0) && (() => {
        const stepperNode = (
          <Box className={s.stepperBar}>
            <Box className={s.stepperInner}>
              {STEPS.map((_, i) => (
                <React.Fragment key={i}>
                  {i > 0 && <Box className={cx(s.stepSeparator, i <= step && s.stepSeparatorDone)} />}
                  <StepIndicator index={i} current={step} steps={STEPS} />
                </React.Fragment>
              ))}
            </Box>
          </Box>
        );
        return <>
          {/* Step content */}
          <Box style={{ display: step === STEP_TP ? "contents" : "none" }}>
            <StepChooseTP onSelect={handleTPSelected} reopenAttributes={reopenAttrs} stepper={stepperNode} />
          </Box>
          {step === STEP_CONTEXT && (
            <StepChooseContext
              thirdparty={selectedTP}
              stepper={stepperNode}
              onSelect={handleContextSelected}
              onSkip={handleContextSkipped}
              onBack={() => {
                if (preSelectedTP) {
                  history.push(getUrlWithAccountId(state.account.id, `light/thirdparties/${preSelectedTP.thirdpartyId}`));
                } else {
                  if (selectedTP?.pendingAttributes?.length > 0) setReopenAttrs(selectedTP);
                  setStep(STEP_TP);
                }
              }}
            />
          )}
          {step === STEP_DOSSIER && (
            <StepChooseDossier
              thirdparty={selectedTP}
              onBack={() => {
                setBackFromConfirm(false);
                if (hasContextModels) {
                  setStep(STEP_CONTEXT);
                } else if (preSelectedTP) {
                  history.push(getUrlWithAccountId(state.account.id, `light/thirdparties/${preSelectedTP.thirdpartyId}`));
                } else {
                  if (selectedTP?.pendingAttributes?.length > 0) setReopenAttrs(selectedTP);
                  setStep(STEP_TP);
                }
              }}
              onSelect={handleDossierSelected}
              initialType={backFromConfirm ? selectedDossierTypes : null}
              backLabel={!hasContextModels && preSelectedTP ? i18next.t("light.launch.backToTpName", { name: preSelectedTP.name }) : null}
              stepper={stepperNode}
            />
          )}
          {step === STEP_CONFIRM && (
            <StepConfirm
              thirdparty={selectedTP}
              dossierTypes={selectedDossierTypes}
              selectedContext={selectedContext}
              onBack={() => { setBackFromConfirm(true); setStep(STEP_DOSSIER); }}
              onLaunch={handleLaunch}
              launching={launching}
              stepper={stepperNode}
            />
          )}
        </>;
      })()}

      {showContactPrompt && (
        <ContactPromptScreen
          accountId={state.account.id}
          thirdpartyId={createdThirdpartyId}
          thirdpartyName={selectedTP?.name}
          dispatch={dispatch}
          onDone={() => history.push(getUrlWithAccountId(state.account.id, `light/thirdparties/${createdThirdpartyId}`))}
          onSkip={() => history.push(getUrlWithAccountId(state.account.id, `light/thirdparties/${createdThirdpartyId}`))}
        />
      )}
    </Box>
  );
}
